import { BOOKING_TYPES, type BookingType, SPACE_KINDS, type SpaceKind } from '@budovan/contracts';
import {
  Anchor,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  Drawer,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { api, unwrap } from '../api.ts';
import { bookingTypeLabel, nextCode, spaceKindLabel } from '../format.ts';
import { Empty, Failure, Loading, Page, useMe, useSave } from '../ui.tsx';

const PREFIX: Record<SpaceKind, string> = {
  meeting_room: 'MR',
  desk: 'D',
  workspace_area: 'WA',
  parking_spot: 'P',
  lobby: 'L',
  point_of_interest: 'POI',
  unit: 'U',
};

const SLOT_MINUTES = ['5', '10', '15', '30', '60'] as const;

type Building = {
  id: string;
  code: string;
  name: string;
  organizationId: string;
  timeZone: string;
  address: string | null;
  floors: { id: string; number: number; name: string }[];
};
type Space = {
  id: string;
  code: string;
  name: string;
  kind: SpaceKind;
  floorId: string | null;
  capacity: number | null;
  isBookable: boolean;
  amenities: string[];
};

function SpaceForm(props: { building: Building; spaces: Space[]; space: Space | null; onDone: () => void }) {
  const { building, space } = props;
  const [form, setForm] = useState({
    kind: space?.kind ?? ('desk' as SpaceKind),
    floorId: space?.floorId ?? building.floors[0]?.id ?? '',
    name: space?.name ?? '',
    capacity: space?.capacity ?? ('' as number | ''),
    isBookable: space?.isBookable ?? true,
    amenities: space?.amenities.join(', ') ?? '',
  });
  const floor = building.floors.find((candidate) => candidate.id === form.floorId);
  const code =
    space?.code ??
    nextCode(
      props.spaces.map((candidate) => candidate.code),
      `${building.code}-F${String(floor?.number ?? 0).padStart(2, '0')}-${PREFIX[form.kind]}`,
    );
  const amenities = form.amenities
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
  const save = useSave({
    run: () =>
      space
        ? unwrap(
            api().PATCH('/v1/spaces/{spaceId}', {
              params: { path: { spaceId: space.id } },
              body: {
                name: form.name,
                isBookable: form.isBookable,
                amenities,
                ...(form.floorId ? { floorId: form.floorId } : {}),
                ...(form.capacity === '' ? {} : { capacity: form.capacity }),
              },
            }),
          )
        : unwrap(
            api().POST('/v1/buildings/{buildingId}/spaces', {
              params: { path: { buildingId: building.id } },
              body: {
                code,
                kind: form.kind,
                name: form.name,
                isBookable: form.isBookable,
                amenities,
                ...(form.floorId ? { floorId: form.floorId } : {}),
                ...(form.capacity === '' ? {} : { capacity: form.capacity }),
              },
            }),
          ),
    invalidates: [['spaces', building.id]],
    success: (saved) => `${saved.name} saved`,
    onDone: props.onDone,
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(undefined);
  };
  return (
    <form onSubmit={submit}>
      <Stack>
        <Select
          label="Kind"
          disabled={space !== null}
          data={SPACE_KINDS.map((kind) => ({ value: kind, label: spaceKindLabel(kind) }))}
          value={form.kind}
          onChange={(kind) => kind && setForm({ ...form, kind: kind as SpaceKind })}
          allowDeselect={false}
        />
        <Select
          label="Floor"
          data={building.floors.map((candidate) => ({ value: candidate.id, label: candidate.name }))}
          value={form.floorId || null}
          onChange={(floorId) => setForm({ ...form, floorId: floorId ?? '' })}
          placeholder="No floor"
          clearable
        />
        <TextInput
          label="Name"
          required
          value={form.name}
          onChange={(event) => setForm({ ...form, name: event.currentTarget.value })}
        />
        <NumberInput
          label="Capacity"
          min={1}
          value={form.capacity}
          onChange={(capacity) =>
            setForm({ ...form, capacity: typeof capacity === 'number' ? capacity : '' })
          }
        />
        <TextInput
          label="Amenities"
          description="Comma separated, e.g. screen, whiteboard, video call"
          value={form.amenities}
          onChange={(event) => setForm({ ...form, amenities: event.currentTarget.value })}
        />
        <Switch
          label="Bookable"
          checked={form.isBookable}
          onChange={(event) => setForm({ ...form, isBookable: event.currentTarget.checked })}
        />
        <Text size="sm" c="dimmed">
          Place code <code>{code}</code>
          {space ? '' : ' (the twin and sensors find the place by it)'}
        </Text>
        <Failure error={save.error} />
        <Button type="submit" loading={save.isPending}>
          {space ? 'Save' : 'Add space'}
        </Button>
      </Stack>
    </form>
  );
}

function SpacesTab(props: { building: Building; canEdit: boolean }) {
  const { building } = props;
  const spaces = useQuery({
    queryKey: ['spaces', building.id],
    queryFn: () =>
      unwrap(
        api().GET('/v1/buildings/{buildingId}/spaces', { params: { path: { buildingId: building.id } } }),
      ),
  });
  const [kind, setKind] = useState<string | null>(null);
  const [editing, setEditing] = useState<Space | 'new' | null>(null);
  const floorName = (id: string | null) => building.floors.find((floor) => floor.id === id)?.name ?? '—';
  const shown = (spaces.data ?? []).filter((space) => !kind || space.kind === kind);
  return (
    <Stack>
      <Group justify="space-between">
        <Select
          placeholder="All kinds"
          clearable
          data={SPACE_KINDS.map((value) => ({ value, label: spaceKindLabel(value) }))}
          value={kind}
          onChange={setKind}
          w={220}
          aria-label="Filter by kind"
        />
        {props.canEdit ? (
          <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing('new')}>
            Add space
          </Button>
        ) : null}
      </Group>
      {spaces.isPending ? <Loading /> : null}
      <Failure error={spaces.error} />
      {spaces.data && shown.length === 0 ? <Empty>No spaces yet.</Empty> : null}
      {shown.length > 0 ? (
        <Table.ScrollContainer minWidth={720}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Code</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Kind</Table.Th>
                <Table.Th>Floor</Table.Th>
                <Table.Th>Capacity</Table.Th>
                <Table.Th>Bookable</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {shown.map((space) => (
                <Table.Tr key={space.id}>
                  <Table.Td>
                    <Text ff="monospace" size="sm">
                      {space.code}
                    </Text>
                  </Table.Td>
                  <Table.Td>{space.name}</Table.Td>
                  <Table.Td>{spaceKindLabel(space.kind)}</Table.Td>
                  <Table.Td>{floorName(space.floorId)}</Table.Td>
                  <Table.Td>{space.capacity ?? '—'}</Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={space.isBookable ? 'green' : 'gray'}>
                      {space.isBookable ? 'Bookable' : 'Not bookable'}
                    </Badge>
                  </Table.Td>
                  <Table.Td ta="right">
                    {props.canEdit ? (
                      <Button size="xs" variant="default" onClick={() => setEditing(space)}>
                        Edit
                      </Button>
                    ) : null}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      ) : null}
      <Drawer
        opened={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'New space' : 'Edit space'}
        position="right"
      >
        {editing !== null ? (
          <SpaceForm
            building={building}
            spaces={spaces.data ?? []}
            space={editing === 'new' ? null : editing}
            onDone={() => setEditing(null)}
          />
        ) : null}
      </Drawer>
    </Stack>
  );
}

function FloorsTab(props: { building: Building; canEdit: boolean }) {
  const { building } = props;
  const [form, setForm] = useState({ number: (building.floors.at(-1)?.number ?? -1) + 1, name: '' });
  const create = useSave({
    run: () =>
      unwrap(
        api().POST('/v1/buildings/{buildingId}/floors', {
          params: { path: { buildingId: building.id } },
          body: { number: form.number, name: form.name || `Floor ${form.number}` },
        }),
      ),
    invalidates: [['building', building.id]],
    success: (floor) => `${floor.name} added`,
    onDone: () => setForm({ number: form.number + 1, name: '' }),
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate(undefined);
  };
  return (
    <Stack>
      {building.floors.length === 0 ? <Empty>No floors yet.</Empty> : null}
      {building.floors.length > 0 ? (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Number</Table.Th>
              <Table.Th>Name</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {building.floors.map((floor) => (
              <Table.Tr key={floor.id}>
                <Table.Td>{floor.number}</Table.Td>
                <Table.Td>{floor.name}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      ) : null}
      {props.canEdit ? (
        <form onSubmit={submit}>
          <Group align="flex-end">
            <NumberInput
              label="Number"
              min={-20}
              max={200}
              w={120}
              value={form.number}
              onChange={(number) => setForm({ ...form, number: Number(number) })}
            />
            <TextInput
              label="Name"
              placeholder={`Floor ${form.number}`}
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.currentTarget.value })}
            />
            <Button type="submit" loading={create.isPending}>
              Add floor
            </Button>
          </Group>
        </form>
      ) : null}
    </Stack>
  );
}

type Policy = {
  bookingType: BookingType;
  minDurationMinutes: number;
  maxDurationMinutes: number;
  maxDaysInAdvance: number;
  slotMinutes: number;
  maxActiveBookings: number | null;
};

function PolicyForm(props: {
  buildingId: string;
  bookingType: BookingType;
  policy: Policy | null;
  onDone: () => void;
}) {
  const [form, setForm] = useState({
    minDurationMinutes: props.policy?.minDurationMinutes ?? 15,
    maxDurationMinutes: props.policy?.maxDurationMinutes ?? 480,
    maxDaysInAdvance: props.policy?.maxDaysInAdvance ?? 30,
    slotMinutes: String(props.policy?.slotMinutes ?? 15) as (typeof SLOT_MINUTES)[number],
    maxActiveBookings: props.policy?.maxActiveBookings ?? ('' as number | ''),
  });
  const save = useSave({
    run: () =>
      unwrap(
        api().PUT('/v1/booking-policies', {
          body: {
            buildingId: props.buildingId,
            bookingType: props.bookingType,
            minDurationMinutes: form.minDurationMinutes,
            maxDurationMinutes: form.maxDurationMinutes,
            maxDaysInAdvance: form.maxDaysInAdvance,
            slotMinutes: Number(form.slotMinutes) as 5 | 10 | 15 | 30 | 60,
            ...(form.maxActiveBookings === '' ? {} : { maxActiveBookings: form.maxActiveBookings }),
          },
        }),
      ),
    invalidates: [['policies', props.buildingId]],
    success: 'Booking rules saved',
    onDone: props.onDone,
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(undefined);
  };
  const number =
    (key: 'minDurationMinutes' | 'maxDurationMinutes' | 'maxDaysInAdvance') => (value: string | number) =>
      setForm({ ...form, [key]: Number(value) });
  return (
    <form onSubmit={submit}>
      <Stack>
        <NumberInput
          label="Shortest booking (minutes)"
          min={1}
          max={1440}
          value={form.minDurationMinutes}
          onChange={number('minDurationMinutes')}
        />
        <NumberInput
          label="Longest booking (minutes)"
          min={1}
          max={10080}
          value={form.maxDurationMinutes}
          onChange={number('maxDurationMinutes')}
        />
        <NumberInput
          label="Book up to (days ahead)"
          min={0}
          max={730}
          value={form.maxDaysInAdvance}
          onChange={number('maxDaysInAdvance')}
        />
        <Select
          label="Time slots (minutes)"
          data={[...SLOT_MINUTES]}
          value={form.slotMinutes}
          onChange={(slot) =>
            slot && setForm({ ...form, slotMinutes: slot as (typeof SLOT_MINUTES)[number] })
          }
          allowDeselect={false}
        />
        <NumberInput
          label="Active bookings per person"
          description="Empty for no limit"
          min={1}
          max={1000}
          value={form.maxActiveBookings}
          onChange={(value) =>
            setForm({ ...form, maxActiveBookings: typeof value === 'number' ? value : '' })
          }
        />
        <Failure error={save.error} />
        <Button type="submit" loading={save.isPending}>
          Save rules
        </Button>
      </Stack>
    </form>
  );
}

function RulesTab(props: { building: Building; canEdit: boolean }) {
  const { building } = props;
  const policies = useQuery({
    queryKey: ['policies', building.id],
    queryFn: () =>
      unwrap(
        api().GET('/v1/buildings/{buildingId}/booking-policies', {
          params: { path: { buildingId: building.id } },
        }),
      ),
  });
  const [editing, setEditing] = useState<BookingType | null>(null);
  const policyFor = (type: BookingType) =>
    policies.data?.find((policy) => policy.bookingType === type) ?? null;
  if (policies.isPending) return <Loading />;
  return (
    <Stack>
      <Failure error={policies.error} />
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Booking type</Table.Th>
            <Table.Th>Duration</Table.Th>
            <Table.Th>Slots</Table.Th>
            <Table.Th>Days ahead</Table.Th>
            <Table.Th>Per person</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {BOOKING_TYPES.map((type) => {
            const policy = policyFor(type);
            return (
              <Table.Tr key={type}>
                <Table.Td fw={600}>{bookingTypeLabel(type)}</Table.Td>
                <Table.Td>
                  {policy ? `${policy.minDurationMinutes}–${policy.maxDurationMinutes} min` : 'Default'}
                </Table.Td>
                <Table.Td>{policy ? `${policy.slotMinutes} min` : '—'}</Table.Td>
                <Table.Td>{policy?.maxDaysInAdvance ?? '—'}</Table.Td>
                <Table.Td>{policy?.maxActiveBookings ?? 'No limit'}</Table.Td>
                <Table.Td ta="right">
                  {props.canEdit ? (
                    <Button size="xs" variant="default" onClick={() => setEditing(type)}>
                      {policy ? 'Edit' : 'Set rules'}
                    </Button>
                  ) : null}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      <Drawer
        opened={editing !== null}
        onClose={() => setEditing(null)}
        title={editing ? `${bookingTypeLabel(editing)} booking rules` : ''}
        position="right"
      >
        {editing ? (
          <PolicyForm
            buildingId={building.id}
            bookingType={editing}
            policy={policyFor(editing)}
            onDone={() => setEditing(null)}
          />
        ) : null}
      </Drawer>
    </Stack>
  );
}

function AccessTab(props: { building: Building; canEdit: boolean }) {
  const { building } = props;
  const systems = useQuery({
    queryKey: ['access-systems', building.id],
    queryFn: () =>
      unwrap(
        api().GET('/v1/buildings/{buildingId}/access-systems', {
          params: { path: { buildingId: building.id } },
        }),
      ),
  });
  const [opened, modal] = useDisclosure(false);
  const [form, setForm] = useState({ provider: 'salto' as 'salto' | 'hid' | 'fake', name: '' });
  const create = useSave({
    run: () =>
      unwrap(
        api().POST('/v1/buildings/{buildingId}/access-systems', {
          params: { path: { buildingId: building.id } },
          body: form,
        }),
      ),
    invalidates: [['access-systems', building.id]],
    success: 'Access system added',
    onDone: modal.close,
  });
  const lots = useSave({
    run: (name: string) =>
      unwrap(
        api().POST('/v1/buildings/{buildingId}/parking-lots', {
          params: { path: { buildingId: building.id } },
          body: { name },
        }),
      ),
    invalidates: [],
    success: (lot) => `Parking lot ${lot.name} added; assign parking spots to it from Spaces`,
  });
  return (
    <Stack>
      <Group justify="space-between">
        <Text fw={600}>Door access</Text>
        {props.canEdit ? (
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={modal.open}>
            Add system
          </Button>
        ) : null}
      </Group>
      {systems.isPending ? <Loading /> : null}
      <Failure error={systems.error} />
      {systems.data?.length === 0 ? <Empty>No access control system connected.</Empty> : null}
      {systems.data?.map((system) => (
        <Card key={system.id} withBorder padding="sm">
          <Group justify="space-between">
            <Text fw={600}>{system.name}</Text>
            <Badge variant="light" color="ink">
              {system.provider.toUpperCase()}
            </Badge>
          </Group>
        </Card>
      ))}
      {props.canEdit ? (
        <>
          <Text fw={600} mt="md">
            Parking
          </Text>
          <Group>
            <Button
              variant="default"
              size="xs"
              loading={lots.isPending}
              onClick={() => lots.mutate(`${building.name} garage`)}
            >
              Add a parking lot
            </Button>
            <Text size="xs" c="dimmed">
              Spots are spaces of kind Parking spot.
            </Text>
          </Group>
        </>
      ) : null}
      <Modal opened={opened} onClose={modal.close} title="Add access control system" centered>
        <Stack>
          <Select
            label="Provider"
            data={[
              { value: 'salto', label: 'Salto' },
              { value: 'hid', label: 'HID Origo' },
              { value: 'fake', label: 'Simulated (demo)' },
            ]}
            value={form.provider}
            onChange={(provider) =>
              provider && setForm({ ...form, provider: provider as typeof form.provider })
            }
            allowDeselect={false}
          />
          <TextInput
            label="Name"
            placeholder="Main entrance"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.currentTarget.value })}
          />
          <Failure error={create.error} />
          <Button loading={create.isPending} disabled={!form.name} onClick={() => create.mutate(undefined)}>
            Add
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}

export function BuildingPage(props: { buildingId: string }) {
  const { buildingId } = props;
  const me = useMe();
  const search = useSearch({ from: '/buildings/$buildingId' });
  const navigate = useNavigate({ from: '/buildings/$buildingId' });
  const building = useQuery({
    queryKey: ['building', buildingId],
    queryFn: () => unwrap(api().GET('/v1/buildings/{buildingId}', { params: { path: { buildingId } } })),
  });
  if (building.isPending) return <Loading />;
  if (building.error) return <Failure error={building.error} />;
  const data = building.data;
  // Only the owner (or an operator) changes a building; row-level security refuses anyone else anyway.
  const canEdit = Boolean(me.data && (me.data.isOperator || me.data.organizationId === data.organizationId));
  return (
    <Page
      title={data.name}
      description={
        <>
          {data.code} · {data.address ?? 'No address'} · {data.timeZone}
          {canEdit ? null : ' · read only: your organization has a contract here'}
        </>
      }
    >
      <Breadcrumbs>
        <Anchor component={Link} to="/buildings" size="sm">
          Buildings
        </Anchor>
        <Text size="sm">{data.name}</Text>
      </Breadcrumbs>
      <Card withBorder padding="lg">
        <Tabs
          value={search.tab ?? 'spaces'}
          onChange={(tab) => void navigate({ search: { tab: tab ?? undefined }, replace: true })}
        >
          <Tabs.List mb="md">
            <Tabs.Tab value="spaces">Spaces</Tabs.Tab>
            <Tabs.Tab value="floors">Floors ({data.floors.length})</Tabs.Tab>
            <Tabs.Tab value="rules">Booking rules</Tabs.Tab>
            <Tabs.Tab value="access">Access and parking</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="spaces">
            <SpacesTab building={data} canEdit={canEdit} />
          </Tabs.Panel>
          <Tabs.Panel value="floors">
            <FloorsTab building={data} canEdit={canEdit} />
          </Tabs.Panel>
          <Tabs.Panel value="rules">
            <RulesTab building={data} canEdit={canEdit} />
          </Tabs.Panel>
          <Tabs.Panel value="access">
            <AccessTab building={data} canEdit={canEdit} />
          </Tabs.Panel>
        </Tabs>
      </Card>
    </Page>
  );
}
